import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { blockToTableData, rowColour, type TableData } from "@/components/editor/table-grid/tableModel";
import { PreviewCellText } from "./TablePreviewPane";
import { headerWithUnit, planColumns, type ColumnPlan } from "@/components/editor/table-grid/tableFormat";
import { DecimalNumber, planCell } from "@/components/editor/table-grid/DecimalNumber";
import { isNumericColumn, layoutTile, tileText, type TileLayout, type TileMetrics, type TileText } from "./tableTiles";
import { hexToRgba } from "@/lib/themes/look";
import { bandedRow, densityScale, type PaperTableStyle } from "@/lib/themes/tables";
import { groupStarts, indexInGroup, marksByColumn, noteMarks, validGroups } from "@/components/editor/table-grid/tableLayout";
import { GroupHeaderRow, NoteMark, TableNotes } from "@/components/editor/table-grid/TableLayoutParts";
import { testIds } from "@/testIds";

/**
 * The table, typeset and tile-clipped: the one preview the insert sheet, the
 * list's cards and its rows all show (Olivia, tables-modern build order 2).
 *
 * Booktabs, serif, numbers on the right, exactly as it will print. That is the
 * modern part: no card of metadata, just the thing itself. It never scales
 * type; the box decides how much shows, by the rules in `tableTiles`.
 */

export type PreviewVariant = "sheet" | "card" | "thumb";

/**
 * A column's alignment in the preview: a decimal column (2g) centres its block
 * of numbers split at the point, as siunitx does; otherwise numbers sit on the
 * right unless the author centred them.
 */
function columnAlign(a: string | undefined, plan: ColumnPlan | undefined, numeric: boolean): React.CSSProperties["textAlign"] {
  if (plan?.decimal) return "center";
  if (a === "r" || (a !== "c" && numeric)) return "right";
  return a === "c" ? "center" : "left";
}

/** A cell as its column means it: the unit in the header, decimal numbers split, the best bold, its note's mark. */
function PlannedCellText({ value, plan, head, mark }: { value: string; plan: ColumnPlan | undefined; head: boolean; mark?: string }) {
  if (head) return <><PreviewCellText value={headerWithUnit(value, plan?.unit)} /><NoteMark mark={mark} /></>;
  const cell = planCell(plan, value);
  if (cell.number && plan) return <><DecimalNumber n={cell.number} plan={plan} bold={cell.best} /><NoteMark mark={mark} /></>;
  return (
    <>
      {cell.best ? <strong data-best="true"><PreviewCellText value={cell.text} /></strong> : <PreviewCellText value={cell.text} />}
      <NoteMark mark={mark} />
    </>
  );
}

/** Sizes from the drawing: the sheet at 13.5 on 34 px rows, the card at 11.5 on 25. */
const METRICS: Record<PreviewVariant, Omit<TileMetrics, "width" | "height"> & { radius: number; rowPad: number }> = {
  sheet: { fontPx: 13.5, headerH: 36, rowH: 34, cellPadX: 16, radius: 10, rowPad: 8 },
  card: { fontPx: 11.5, headerH: 27, rowH: 25, cellPadX: 12, radius: 7, rowPad: 5 },
  thumb: { fontPx: 7.5, headerH: 12, rowH: 10, cellPadX: 6, radius: 4, rowPad: 2, minProsePx: 20 },
};

export interface PreviewCell {
  /** -1 is the header row. */
  r: number;
  c: number;
}

/** Decode a stored table's content; null when it cannot be read. */
export function readTable(content: unknown): TableData | null {
  try {
    return blockToTableData(content as never);
  } catch {
    return null;
  }
}

/** The layout a preview of this size gets: what shows, and what is counted. */
export function previewLayout(text: TileText, variant: PreviewVariant, width: number, height: number): TileLayout {
  const m = METRICS[variant];
  return layoutTile(text, { ...m, width: Math.max(0, width - 2 * m.rowPad - 2), height: Math.max(0, height - 2) });
}

/** The width of an element, followed as it resizes; `fallback` until measured. */
export function useBoxWidth(fallback: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => { if (el.clientWidth > 0) setWidth(el.clientWidth); };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

/**
 * The whole table, nothing clipped: the tables page's detail (Olivia,
 * tables-modern turn 2, `clip="none"`). The sheet's type, every column and
 * every row, and the caption under it as it will print. Read-only: a double
 * click on a cell asks to open the tool there.
 *
 * With `paper` (Preview as, document themes 2a) it is drawn on that paper the
 * way the paper prints it: header face and weight, top rule colour, body face,
 * and the paper's table style, density and caption position. Content, widths
 * and alignment never change.
 */
function WholeTable({
  data,
  caption,
  onCellDoubleClick,
  paper,
}: {
  data: TableData;
  caption?: React.ReactNode;
  onCellDoubleClick?: (cell: PreviewCell) => void;
  paper?: PaperTableStyle | null;
}) {
  const m = METRICS.sheet;
  const text = useMemo(() => tileText(data), [data]);
  const numeric = useMemo(
    () => Array.from({ length: text.width }, (_, c) => isNumericColumn(text.rows, c)),
    [text],
  );
  const plan = useMemo(() => planColumns(data, text.width), [data, text.width]);
  // Group headers, notes and row groups (tableLayout.ts).
  const groups = useMemo(() => validGroups(data, text.width), [data, text.width]);
  const marks = useMemo(() => marksByColumn(data), [data]);
  const { notes } = useMemo(() => noteMarks(data), [data]);
  const starts = useMemo(() => groupStarts(data), [data]);
  const align = (c: number): React.CSSProperties["textAlign"] => columnAlign(data.align[c], plan[c], numeric[c]);
  const scale = densityScale(paper?.density ?? "normal");
  const ink = paper ? paper.ink : "var(--lilia-text)";
  const rule = "1.5px solid var(--lilia-text)";
  const hair = paper ? `1px solid ${hexToRgba(paper.ink, 0.14)}` : "1px solid var(--lilia-border)";
  const grid = data.borders === "grid";
  const booktabs = data.borders === "booktabs";
  const fontPx = m.fontPx * scale.font;
  const cell = (value: string, r: number, c: number, head: boolean) => {
    const Tag = head ? "th" : "td";
    return (
      <Tag
        key={c}
        scope={head ? "col" : undefined}
        data-r={r}
        data-c={c}
        onDoubleClick={onCellDoubleClick ? () => onCellDoubleClick({ r, c }) : undefined}
        style={{
          textAlign: align(c),
          padding: `0 ${m.cellPadX * scale.padX}px`,
          height: (head ? m.headerH : m.rowH) * scale.row,
          whiteSpace: "nowrap",
          fontWeight: head ? paper?.headWeight ?? 600 : 400,
          fontSize: head ? fontPx - 0.5 : fontPx,
          borderLeft: grid && c > 0 ? hair : undefined,
        }}
      >
        <PlannedCellText value={value} plan={plan[c]} head={head} mark={marks.get(`${r}:${c}`)} />
      </Tag>
    );
  };
  const captionBlock = caption && (
    <div
      data-testid={testIds.tableTool.tablePreviewCaption}
      data-position={paper?.caption ?? "below"}
      className={paper?.caption === "above" ? "px-4 pb-3 pt-1 text-[12.5px]" : "px-4 pb-1 pt-3 text-[12.5px]"}
      style={{ fontFamily: paper ? paper.bodyFont : "var(--lilia-font-serif)" }}
    >
      {caption}
    </div>
  );
  const headFill = paper?.headFill ?? null;
  return (
    <div
      data-testid={testIds.tableTool.tablePreview}
      data-variant="sheet"
      data-clip="none"
      data-cols-shown={text.width}
      data-rows-shown={text.rows.length}
      data-paper-style={paper?.style}
      data-theme={paper?.themeId}
      className={paper ? "rounded-[3px] px-3 py-2" : undefined}
      style={{
        fontFamily: paper ? paper.bodyFont : "var(--lilia-font-serif)",
        color: ink,
        background: paper?.paper,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {paper?.caption === "above" && captionBlock}
      <div className="overflow-x-auto">
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            cursor: onCellDoubleClick ? "default" : undefined,
            borderTop: paper && booktabs && paper.rule ? `1.5px solid ${paper.rule}` : undefined,
            borderBottom: paper && booktabs ? `1px solid ${paper.ink}` : undefined,
          }}
        >
          {text.header && (
            <thead>
              {groups.length > 0 && (
                <GroupHeaderRow
                  groups={groups}
                  width={text.width}
                  ruleColor={headFill ? paper!.headInk : ink}
                  cellStyle={{ padding: `0 ${m.cellPadX * scale.padX}px`, height: m.headerH * scale.row * 0.8, fontWeight: paper?.headWeight ?? 600, fontSize: fontPx - 0.5, whiteSpace: "nowrap", background: headFill ?? undefined, color: headFill ? paper!.headInk : undefined, fontFamily: paper?.headFont }}
                />
              )}
              <tr
                data-part="head"
                style={{
                  borderBottom: data.borders === "none" || headFill ? undefined : paper ? `0.75px solid ${paper.ink}` : rule,
                  background: headFill ?? undefined,
                  color: headFill ? paper!.headInk : undefined,
                  fontFamily: paper?.headFont,
                }}
              >
                {text.header.map((v, c) => cell(v, -1, c, true))}
              </tr>
            </thead>
          )}
          <tbody>
            {text.rows.map((cells, r) => {
              // Banding restarts after each row group's rule, as lilia-theme.sty does.
              // The table's own row colours win over the paper's bands, as in the PDF.
              const own = data.rowColors ? rowColour(data.rowColors, r) : undefined;
              const band = data.rowColors ? own : paper?.band && bandedRow(indexInGroup(r, starts), text.rows.length) ? paper.band : undefined;
              const groupStart = starts.has(r);
              return (
                <tr
                  key={r}
                  data-band={band ? "true" : undefined}
                  data-group-start={groupStart || undefined}
                  style={{
                    background: band,
                    borderTop: groupStart ? (grid ? `3px double ${paper ? paper.ink : "var(--lilia-text)"}` : data.borders === "booktabs" ? `0.75px solid ${paper ? paper.ink : "var(--lilia-text)"}` : undefined) : undefined,
                    borderBottom: r === text.rows.length - 1 || paper?.band ? undefined : hair,
                  }}
                >
                  {cells.map((v, c) => cell(v, r, c, false))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Notes print under the table (threeparttable), before a caption set below. */}
      <TableNotes notes={notes} style={{ padding: "4px 16px 0", fontSize: fontPx * 0.82 }} />
      {paper?.caption !== "above" && captionBlock}
    </div>
  );
}

export function TablePreview({
  data,
  variant,
  width,
  height,
  highlight,
  flushBottom,
  clip = "tile",
  caption,
  onCellDoubleClick,
  paper,
}: {
  data: TableData;
  variant: PreviewVariant;
  /** Box size, px. Ignored when nothing is clipped. */
  width: number;
  height: number;
  /** A search match inside the table: that cell lights up. */
  highlight?: PreviewCell | null;
  /** The card's preview runs off its bottom edge, so it has no bottom border. */
  flushBottom?: boolean;
  /** `none`: the whole table, every column and row (the tables page's detail). */
  clip?: "tile" | "none";
  /** Under the whole table, as it prints. Only with `clip="none"`. */
  caption?: React.ReactNode;
  /** With `clip="none"`: a cell was double-clicked. */
  onCellDoubleClick?: (cell: PreviewCell) => void;
  /** With `clip="none"`: draw it as this paper prints it (Preview as). */
  paper?: PaperTableStyle | null;
}) {
  if (clip === "none") return <WholeTable data={data} caption={caption} onCellDoubleClick={onCellDoubleClick} paper={paper} />;
  return <TilePreview data={data} variant={variant} width={width} height={height} highlight={highlight} flushBottom={flushBottom} />;
}

function TilePreview({
  data,
  variant,
  width,
  height,
  highlight,
  flushBottom,
}: {
  data: TableData;
  variant: PreviewVariant;
  width: number;
  height: number;
  highlight?: PreviewCell | null;
  flushBottom?: boolean;
}) {
  const m = METRICS[variant];
  const text = useMemo(() => tileText(data), [data]);
  const layout = useMemo(() => previewLayout(text, variant, width, height), [text, variant, width, height]);
  const plan = useMemo(() => planColumns(data, text.width), [data, text.width]);
  const marks = useMemo(() => marksByColumn(data), [data]);
  const starts = useMemo(() => groupStarts(data), [data]);
  const cols = layout.columns;
  const template = cols.map((c) => `minmax(0, ${c.width}fr)`).join(" ");

  const align = (col: (typeof cols)[number]): React.CSSProperties["textAlign"] =>
    columnAlign(data.align[col.index], plan[col.index], col.numeric);
  const lit = (r: number, c: number) => highlight && highlight.r === r && highlight.c === c;

  const row = (cells: string[], r: number, h: number, head: boolean, last: boolean) => (
    <div
      className="grid items-center"
      style={{
        gridTemplateColumns: template,
        height: h,
        padding: `0 ${m.rowPad}px`,
        fontWeight: head ? 600 : undefined,
        fontSize: head ? m.fontPx - 0.5 : m.fontPx,
        borderBottom: head
          ? data.borders === "none" ? undefined : `${variant === "thumb" ? 1 : 1.5}px solid var(--lilia-text)`
          : last ? undefined : starts.has(r + 1) ? "1px solid var(--lilia-text)" : "1px solid var(--lilia-border)",
      }}
    >
      {cols.map((col, i) => (
        <div
          key={col.index}
          data-lit={lit(r, col.index) || undefined}
          className="min-w-0 truncate"
          style={{
            textAlign: align(col),
            padding: `0 ${m.cellPadX / 2}px`,
            borderLeft: data.borders === "grid" && i > 0 ? "1px solid var(--lilia-border)" : undefined,
            ...(lit(r, col.index)
              ? { background: "var(--lilia-primary-subtle)", boxShadow: "inset 0 0 0 1px var(--lilia-primary)", borderRadius: 3 }
              : null),
          }}
        >
          <PlannedCellText value={cells[col.index] ?? ""} plan={plan[col.index]} head={head} mark={marks.get(`${r}:${col.index}`)} />
        </div>
      ))}
    </div>
  );

  const shown = text.rows.slice(0, layout.fullRows);
  const half = layout.halfRow ? text.rows[layout.fullRows] : null;

  return (
    <div
      data-testid={testIds.tableTool.tablePreview}
      data-variant={variant}
      data-cols-shown={cols.length}
      data-rows-shown={layout.fullRows}
      data-half-row={layout.halfRow || undefined}
      className="overflow-hidden"
      style={{
        maxHeight: height,
        background: "var(--lilia-bg)",
        border: "1px solid var(--lilia-border)",
        borderBottom: flushBottom ? "none" : undefined,
        borderRadius: flushBottom ? `${m.radius}px ${m.radius}px 0 0` : m.radius,
        fontFamily: "var(--lilia-font-serif)",
        color: "var(--lilia-text)",
        fontVariantNumeric: "tabular-nums",
        pointerEvents: "none",
      }}
    >
      {text.header && row(text.header, -1, m.headerH, true, false)}
      {shown.map((cells, r) => (
        <div key={r}>{row(cells, r, m.rowH, false, r === shown.length - 1 && !half)}</div>
      ))}
      {/* Half a row at the clip line says "there is more"; nothing is drawn over the table. */}
      {half && (
        <div aria-hidden style={{ height: m.rowH / 2, overflow: "hidden", opacity: 0.55 }}>
          {row(half, layout.fullRows, m.rowH, false, true)}
        </div>
      )}
    </div>
  );
}
